import { AlertTriangle, XCircle, FileX, Clock, MapPin } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

interface DemoAlert {
  id:         string
  severity:   'high' | 'medium' | 'low'
  type:       string
  message:    string
  parcel:     string
  activity:   string
  created_at: string
}

interface Props {
  alerts:  DemoAlert[]
  loading: boolean
  maxRows?: number
}

const SEVERITY_STYLES: Record<string, string> = {
  high:   'border-l-red-400 bg-red-50',
  medium: 'border-l-orange-400 bg-amber-50',
  low:    'border-l-blue-400 bg-blue-50',
}

const SEVERITY_ICON_COLOR: Record<string, string> = {
  high:   'text-red-500',
  medium: 'text-orange-500',
  low:    'text-blue-500',
}

const TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  BLOCKED:            XCircle,
  PENDING_VALIDATION: Clock,
  REJECTED_DOC:       FileX,
  BEHIND_SCHEDULE:    MapPin,
  PENDING_DOC:        Clock,
}

function AlertRow({ alert }: { alert: DemoAlert }) {
  const Icon = TYPE_ICONS[alert.type] ?? AlertTriangle
  const timeAgo = formatDistanceToNow(new Date(alert.created_at), { addSuffix: true, locale: es })

  return (
    <div className={`flex items-start gap-3 p-3.5 border-l-4 rounded-r-lg ${SEVERITY_STYLES[alert.severity]}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${SEVERITY_ICON_COLOR[alert.severity]}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-800 leading-snug">{alert.message}</p>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-gray-500">{alert.parcel}</span>
          {alert.activity !== '—' && (
            <span className="text-xs text-gray-400">· {alert.activity}</span>
          )}
          <span className="text-xs text-gray-400 ml-auto shrink-0">{timeAgo}</span>
        </div>
      </div>
    </div>
  )
}

export default function AlertsTable({ alerts, loading, maxRows = 10 }: Props) {
  const displayed = alerts.slice(0, maxRows)

  return (
    <div className="card p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Alertas e incidencias</h3>
          <p className="text-xs text-gray-400 mt-0.5">{alerts.length} activas</p>
        </div>
        <div className="flex gap-1.5">
          {(['high','medium','low'] as const).map(sev => {
            const count = alerts.filter(a => a.severity === sev).length
            if (!count) return null
            const cls = sev === 'high' ? 'bg-red-100 text-red-700' : sev === 'medium' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
            const label = sev === 'high' ? 'Alta' : sev === 'medium' ? 'Media' : 'Baja'
            return (
              <span key={sev} className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>
                {count} {label}
              </span>
            )
          })}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : displayed.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-sm text-gray-400">
          Sin alertas activas ✓
        </div>
      ) : (
        <div className="space-y-2">
          {displayed.map(alert => <AlertRow key={alert.id} alert={alert} />)}
        </div>
      )}
    </div>
  )
}
